import React, { useCallback, useRef, useState } from 'react';
import { isEmpty, isEqual, isFunction, map, noop } from 'lodash';
import PropTypes from 'prop-types';
import Tooltip from '@material-ui/core/Tooltip';
import DataTable from '../data-table/DataTable';
import { useSelectorStyles } from './ListSelectorStyles';
import { SELECTIONMODE_MULTI, SELECTIONMODE_SINGLE } from '../data-table/SelectionContext';
import { ID_FIELD } from './FormConstants';
import NgpFormButton from './NgpFormButton';
import { useArrayLoader } from '../data-table/Loaders';
import { filterEntities } from './ListSelectorUtils';
import ActionLinearProgress from '../linear-progress/ActionLinearProgress';

const ListActionTable = ({
    tableId,
    // eslint-disable-next-line no-unused-vars
    lastLoadDataArrayTime,
    emptyText,
    loader,
    columns,
    sorters,
    selectionOptions,
    visibleRows,
    onStateChanged,
}) => {
    return (
        <DataTable
            tableId={tableId}
            emptyText={emptyText}
            loader={loader}
            columns={columns}
            sorters={sorters}
            selectionOptions={selectionOptions}
            visibleRows={visibleRows}
            disableColumnRemover
            showSelectionColumn
            onStateChanged={onStateChanged}
        />
    );
};

ListActionTable.propTypes = {
    lastLoadDataArrayTime: PropTypes.number,
    tableId: PropTypes.string,
    emptyText: PropTypes.string,
    loader: PropTypes.func,
    columns: PropTypes.array,
    sorters: PropTypes.array,
    // eslint-disable-next-line react/require-default-props
    selectionOptions: PropTypes.object,
    visibleRows: PropTypes.number,
    onStateChanged: PropTypes.func,
};

ListActionTable.defaultProps = {
    lastLoadDataArrayTime: -1,
    tableId: '',
    emptyText: '',
    columns: [],
    loader: () => {},
    sorters: [],
    selectionOptions: null,
    visibleRows: 10,
    onStateChanged: noop,
};

const MemoListActionTable = React.memo(
    ListActionTable,
    (oldProps, newProps) =>
        isEqual(oldProps.lastLoadDataArrayTime, newProps.lastLoadDataArrayTime) &&
        isEqual(oldProps.emptyText, newProps.emptyText)
);

const ListActionSelector = ({
    tableId,
    emptyText,
    model = SELECTIONMODE_MULTI,
    gridColumns,
    sorters,
    dataArray,
    lastLoadDataArrayTime,
    dataArraySearchFields,
    actionButtons,
    isActioning,
    changeSelectionId,
    visibleRows,
    actionMenus,
    onStateChanged,
}) => {
    const classes = useSelectorStyles();
    const [disabled, setDisabled] = useState(false);
    const [tableSelection, setTableSelection] = useState(undefined);
    const tableRowsRef = useRef(null);

    const staticDataFilterCallback = useCallback(
        (entities, filters) => filterEntities(entities, filters, dataArraySearchFields),
        [dataArraySearchFields]
    );
    const selectedLoader = useArrayLoader(dataArray, staticDataFilterCallback);

    const handleTableSelectionChange = (currentTableSelection, tableRows) => {
        tableRowsRef.current = tableRows;
        setTableSelection(currentTableSelection);
        setDisabled(
            !currentTableSelection || (isEmpty(currentTableSelection.selection) && !currentTableSelection.inverted)
        );
        if (isFunction(changeSelectionId)) {
            changeSelectionId(currentTableSelection);
        }
    };

    const getTableSelection = () => {
        if (!tableSelection?.inverted) {
            return tableSelection;
        }

        const selection = Object.values(tableRowsRef.current || {}).map((item) => item?.row?._id);
        return {
            selection,
            inverted: false,
        };
    };

    const getSelectionOptions = () => {
        if (SELECTIONMODE_SINGLE === model) {
            return {
                mode: SELECTIONMODE_SINGLE,
                needSingleAutoSelectFirst: true,
                allowSelectAll: false,
                idMapping: ID_FIELD,
                onChange: handleTableSelectionChange,
            };
        }

        return {
            mode: SELECTIONMODE_MULTI,
            allowSelectAll: true,
            idMapping: ID_FIELD,
            onChange: handleTableSelectionChange,
        };
    };

    return (
        <div className={classes.selectorContainer}>
            {actionMenus}
            {!isEmpty(actionButtons) && (
                <div className={classes.selectorActions}>
                    {map(actionButtons, (value, key) => (
                        <Tooltip
                            key={key}
                            title={value.title}
                            placement="bottom"
                            classes={{ tooltip: classes.browserLikeTooltip }}
                        >
                            <span>
                                <NgpFormButton
                                    onClick={() => value.actionFn(getTableSelection())}
                                    disabled={disabled || isActioning}
                                    endIcon={value.endIcon}
                                >
                                    {value.label}
                                </NgpFormButton>
                            </span>
                        </Tooltip>
                    ))}
                </div>
            )}
            <ActionLinearProgress isActioning={isActioning} />
            <MemoListActionTable
                lastLoadDataArrayTime={lastLoadDataArrayTime}
                tableId={tableId}
                emptyText={emptyText}
                loader={selectedLoader}
                columns={gridColumns}
                sorters={sorters}
                selectionOptions={getSelectionOptions()}
                visibleRows={visibleRows}
                onStateChanged={onStateChanged}
            />
        </div>
    );
};

ListActionSelector.propTypes = {
    tableId: PropTypes.string,
    emptyText: PropTypes.string,
    model: PropTypes.string,
    gridColumns: PropTypes.array,
    dataArray: PropTypes.array,
    lastLoadDataArrayTime: PropTypes.number,
    dataArraySearchFields: PropTypes.array,
    sorters: PropTypes.array,
    actionButtons: PropTypes.object,
    isActioning: PropTypes.bool,
    changeSelectionId: PropTypes.func,
    visibleRows: PropTypes.number,
    actionMenus: PropTypes.node,
    onStateChanged: PropTypes.func,
};

ListActionSelector.defaultProps = {
    tableId: '',
    emptyText: '',
    model: SELECTIONMODE_MULTI,
    gridColumns: [],
    dataArray: [],
    lastLoadDataArrayTime: -1,
    dataArraySearchFields: [],
    sorters: [],
    actionButtons: {},
    isActioning: false,
    changeSelectionId: () => {},
    visibleRows: 10,
    actionMenus: null,
    onStateChanged: noop,
};

export default React.memo(
    ListActionSelector,
    (oldProps, newProps) =>
        isEqual(oldProps.lastLoadDataArrayTime, newProps.lastLoadDataArrayTime) &&
        isEqual(oldProps.isActioning, newProps.isActioning) &&
        isEqual(oldProps.emptyText, newProps.emptyText) &&
        isEqual(oldProps.actionMenus, newProps.actionMenus)
);
